import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  DragIndicator as DragIcon,
  Title as TitleIcon,
  Image as ImageIcon,
  TextFields as TextIcon,
  SmartButton as ButtonIcon,
  ViewColumn as ContainerIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  DeleteOutline as DeleteOutlineIcon,
  ContentCopy as CopyIcon,
  Category as CategoryIcon,
  Event as EventIcon,
  LocalOffer as TagIcon,
  Business as VendorIcon,
  BarChart as AnalyticsIcon,
  Share as ShareIcon,
  CheckCircle as VerifiedIcon,
  CalendarToday as CalendarIcon,
  VideoCall as VirtualIcon,
  Policy as PolicyIcon,
  Info as InfoIcon,
  AccessTime as TimerIcon
} from '@mui/icons-material';
import { eventDesignService, handleDesignApiError } from '../services/eventDesignService';
import { format } from 'date-fns';

// Component types
const COMPONENT_TYPES = {
  HEADER: 'header',
  IMAGE: 'image',
  TEXT: 'text',
  BUTTON: 'button',
  CONTAINER: 'container',
  INFO_SECTION: 'info_section',
  GALLERY: 'gallery',
  FOOTER: 'footer',
  TICKET_TYPES: 'ticket_types',
  DOWNLOAD_BUTTONS: 'download_buttons',
  SPACER: 'spacer',
  DIVIDER: 'divider',
  CATEGORY_BADGE: 'category_badge',
  EVENT_TYPE_BADGE: 'event_type_badge',
  TAGS: 'tags',
  VENDOR_INFO: 'vendor_info',
  ANALYTICS_STATS: 'analytics_stats',
  SOCIAL_SHARING: 'social_sharing',
  EVENT_STATUS: 'event_status',
  DATE_RANGE: 'date_range',
  VIRTUAL_LINK: 'virtual_link',
  POLICIES: 'policies',
  VERIFICATION_BADGE: 'verification_badge',
  COUNTDOWN_TIMER: 'countdown_timer'
};

// Default components palette
const COMPONENT_PALETTE = [
  { type: COMPONENT_TYPES.HEADER, label: 'Header', icon: <TitleIcon />, description: 'Event title and featured badge' },
  { type: COMPONENT_TYPES.IMAGE, label: 'Cover Image', icon: <ImageIcon />, description: 'Large banner/cover image' },
  { type: COMPONENT_TYPES.INFO_SECTION, label: 'Info Section', icon: <SettingsIcon />, description: 'Venue, date, pricing info' },
  { type: COMPONENT_TYPES.GALLERY, label: 'Gallery', icon: <ImageIcon />, description: 'Image gallery grid' },
  { type: COMPONENT_TYPES.TEXT, label: 'Text Block', icon: <TextIcon />, description: 'Rich text content' },
  { type: COMPONENT_TYPES.TICKET_TYPES, label: 'Ticket Types', icon: <ButtonIcon />, description: 'Ticket types preview' },
  { type: COMPONENT_TYPES.BUTTON, label: 'Button', icon: <ButtonIcon />, description: 'Call-to-action button' },
  { type: COMPONENT_TYPES.DOWNLOAD_BUTTONS, label: 'Download Buttons', icon: <ButtonIcon />, description: 'App store download buttons' },
  { type: COMPONENT_TYPES.CATEGORY_BADGE, label: 'Category Badge', icon: <CategoryIcon />, description: 'Display event category' },
  { type: COMPONENT_TYPES.EVENT_TYPE_BADGE, label: 'Event Type Badge', icon: <EventIcon />, description: 'In-person, virtual, or hybrid' },
  { type: COMPONENT_TYPES.TAGS, label: 'Tags', icon: <TagIcon />, description: 'Event tags/hashtags' },
  { type: COMPONENT_TYPES.VENDOR_INFO, label: 'Vendor Info', icon: <VendorIcon />, description: 'Organizer/vendor information' },
  { type: COMPONENT_TYPES.ANALYTICS_STATS, label: 'Analytics Stats', icon: <AnalyticsIcon />, description: 'Views, shares, favorites' },
  { type: COMPONENT_TYPES.SOCIAL_SHARING, label: 'Social Sharing', icon: <ShareIcon />, description: 'Share buttons for social media' },
  { type: COMPONENT_TYPES.EVENT_STATUS, label: 'Event Status', icon: <InfoIcon />, description: 'Event status badge' },
  { type: COMPONENT_TYPES.DATE_RANGE, label: 'Date Range', icon: <CalendarIcon />, description: 'Start and end dates' },
  { type: COMPONENT_TYPES.VIRTUAL_LINK, label: 'Virtual Link', icon: <VirtualIcon />, description: 'Virtual meeting link' },
  { type: COMPONENT_TYPES.POLICIES, label: 'Policies', icon: <PolicyIcon />, description: 'Terms, refund, cancellation' },
  { type: COMPONENT_TYPES.VERIFICATION_BADGE, label: 'Verification Badge', icon: <VerifiedIcon />, description: 'Verified event badge' },
  { type: COMPONENT_TYPES.COUNTDOWN_TIMER, label: 'Countdown Timer', icon: <TimerIcon />, description: 'Event countdown timer' },
  { type: COMPONENT_TYPES.DIVIDER, label: 'Divider', icon: <ContainerIcon />, description: 'Visual separator line' },
  { type: COMPONENT_TYPES.SPACER, label: 'Spacer', icon: <ContainerIcon />, description: 'Empty space' },
  { type: COMPONENT_TYPES.FOOTER, label: 'Footer', icon: <ContainerIcon />, description: 'Page footer' }
];

const EventDesignTemplates = () => {
  const theme = useTheme();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isDefault: false,
    isActive: true,
    design: {
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      useGradient: true,
      gradientStart: '#667eea',
      gradientEnd: '#764ba2',
      gradientDirection: '135deg',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      headingFontSize: '28px',
      bodyFontSize: '16px',
      borderRadius: '24px',
      cardPadding: '30px 24px',
      buttonStyle: 'gradient',
      buttonBorderRadius: '12px',
      customCSS: ''
    },
    layout: [] // Array of components in order
  });
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [draggedComponent, setDraggedComponent] = useState(null); // For new components from palette
  const [draggedComponentId, setDraggedComponentId] = useState(null); // For reordering existing components
  const [propertiesPanelOpen, setPropertiesPanelOpen] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, [pagination.page]);

  // Keep selectedComponent in sync with layout changes
  useEffect(() => {
    if (selectedComponent) {
      const updatedComponent = formData.layout.find(c => c.id === selectedComponent.id);
      if (updatedComponent && updatedComponent !== selectedComponent) {
        setSelectedComponent(updatedComponent);
      }
    }
  }, [formData.layout]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await eventDesignService.getAllTemplates({
        page: pagination.page,
        limit: 10
      });
      
      if (response.success) {
        setTemplates(response.data);
        setPagination(prev => ({
          ...prev,
          totalPages: response.pagination?.totalPages || 1,
          totalItems: response.pagination?.totalItems || 0
        }));
      }
    } catch (err) {
      setError(handleDesignApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (template = null) => {
    if (template) {
      // If template is just an ID or minimal object, fetch full details
      let fullTemplate = template;
      if (template._id && (!template.design || !template.layout)) {
        try {
          const response = await eventDesignService.getTemplateById(template._id);
          if (response.success) {
            fullTemplate = response.data;
          }
        } catch (err) {
          console.error('Error fetching template details:', err);
        }
      }
      
      setSelectedTemplate(fullTemplate);
      
      // Default design structure
      const defaultDesign = {
        primaryColor: '#667eea',
        secondaryColor: '#764ba2',
        backgroundColor: '#ffffff',
        textColor: '#333333',
        useGradient: true,
        gradientStart: '#667eea',
        gradientEnd: '#764ba2',
        gradientDirection: '135deg',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        headingFontSize: '28px',
        bodyFontSize: '16px',
        borderRadius: '24px',
        cardPadding: '30px 24px',
        buttonStyle: 'gradient',
        buttonBorderRadius: '12px',
        customCSS: ''
      };
      
      // Default layout structure - matches the professional design based on event data
      const defaultLayout = [
        { id: '1', type: COMPONENT_TYPES.HEADER, props: { title: 'Event Title', showFeatured: true } },
        { id: '2', type: COMPONENT_TYPES.IMAGE, props: {} },
        { id: 'spacer-1', type: COMPONENT_TYPES.SPACER, props: { height: '30px' } },
        { id: '3', type: COMPONENT_TYPES.INFO_SECTION, props: { showVenue: true, showDate: true, showPricing: true } },
        { id: '4', type: COMPONENT_TYPES.GALLERY, props: { showTitle: true, columns: 3 } },
        { id: '5', type: COMPONENT_TYPES.TEXT, props: { heading: 'About This Event', content: 'Event description here...' } },
        { id: '6', type: COMPONENT_TYPES.TICKET_TYPES, props: { showTitle: true, maxDisplay: 3 } },
        { id: '7', type: COMPONENT_TYPES.DOWNLOAD_BUTTONS, props: { showIOS: true, showAndroid: true } },
        { id: '8', type: COMPONENT_TYPES.BUTTON, props: { text: 'Open in Utilita App' } },
        { id: 'divider-1', type: COMPONENT_TYPES.DIVIDER, props: { thickness: '1px', color: '#e0e0e0', margin: '20px 0' } },
        { id: '9', type: COMPONENT_TYPES.FOOTER, props: { text: 'Powered by Utilita', showAffiliate: true } }
      ];
      
      // Merge design with defaults
      const mergedDesign = fullTemplate.design 
        ? { ...defaultDesign, ...fullTemplate.design }
        : defaultDesign;
      
      // Use template layout if available and valid, otherwise use default
      const templateLayout = fullTemplate.layout;
      let finalLayout = defaultLayout;
      
      if (templateLayout && Array.isArray(templateLayout) && templateLayout.length > 0) {
        // Validate and ensure all components have required fields
        finalLayout = templateLayout.map((comp, idx) => ({
          id: comp.id || `comp-${idx}-${Date.now()}`,
          type: comp.type || COMPONENT_TYPES.HEADER,
          props: comp.props || {}
        }));
      }
      
      console.log('Loading template:', {
        name: fullTemplate.name,
        hasDesign: !!fullTemplate.design,
        hasLayout: !!fullTemplate.layout,
        templateLayoutLength: templateLayout?.length || 0,
        finalLayoutLength: finalLayout.length,
        finalLayout: finalLayout
      });
      
      setFormData({
        name: fullTemplate.name || '',
        description: fullTemplate.description || '',
        isDefault: fullTemplate.isDefault || false,
        isActive: fullTemplate.isActive !== undefined ? fullTemplate.isActive : true,
        design: mergedDesign,
        layout: finalLayout
      });
    } else {
      setSelectedTemplate(null);
      setFormData({
        name: '',
        description: '',
        isDefault: false,
        isActive: true,
        design: {
          primaryColor: '#667eea',
          secondaryColor: '#764ba2',
          backgroundColor: '#ffffff',
          textColor: '#333333',
          useGradient: true,
          gradientStart: '#667eea',
          gradientEnd: '#764ba2',
          gradientDirection: '135deg',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          headingFontSize: '28px',
          bodyFontSize: '16px',
          borderRadius: '24px',
          cardPadding: '30px 24px',
          buttonStyle: 'gradient',
          buttonBorderRadius: '12px',
          customCSS: ''
        },
        layout: [
          { id: '1', type: COMPONENT_TYPES.HEADER, props: { title: 'Event Title', showFeatured: true } },
          { id: '2', type: COMPONENT_TYPES.IMAGE, props: {} },
          { id: 'spacer-1', type: COMPONENT_TYPES.SPACER, props: { height: '30px' } },
          { id: '3', type: COMPONENT_TYPES.INFO_SECTION, props: { showVenue: true, showDate: true, showPricing: true } },
          { id: '4', type: COMPONENT_TYPES.GALLERY, props: { showTitle: true, columns: 3 } },
          { id: '5', type: COMPONENT_TYPES.TEXT, props: { heading: 'About This Event', content: 'Event description here...' } },
          { id: '6', type: COMPONENT_TYPES.TICKET_TYPES, props: { showTitle: true, maxDisplay: 3 } },
          { id: '7', type: COMPONENT_TYPES.DOWNLOAD_BUTTONS, props: { showIOS: true, showAndroid: true } },
          { id: '8', type: COMPONENT_TYPES.BUTTON, props: { text: 'Open in Utilita App' } },
          { id: 'divider-1', type: COMPONENT_TYPES.DIVIDER, props: { thickness: '1px', color: '#e0e0e0', margin: '20px 0' } },
          { id: '9', type: COMPONENT_TYPES.FOOTER, props: { text: 'Powered by Utilita', showAffiliate: true } }
        ]
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTemplate(null);
    setSelectedComponent(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      let response;
      if (selectedTemplate) {
        response = await eventDesignService.updateTemplate(selectedTemplate._id, formData);
      } else {
        response = await eventDesignService.createTemplate(formData);
      }
      
      if (response.success) {
        handleCloseDialog();
        fetchTemplates();
      }
    } catch (err) {
      setError(handleDesignApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await eventDesignService.deleteTemplate(selectedTemplate._id);
      if (response.success) {
        setDeleteDialogOpen(false);
        setSelectedTemplate(null);
        fetchTemplates();
      }
    } catch (err) {
      setError(handleDesignApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  // Drag and Drop handlers
  const handleDragStart = (e, componentType) => {
    setDraggedComponent(componentType);
    setDraggedComponentId(null); // Clear any existing component drag
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handler for dragging existing components (reordering)
  const handleComponentDragStart = (e, componentId) => {
    e.stopPropagation(); // Prevent event bubbling
    setDraggedComponentId(componentId);
    setDraggedComponent(null); // Clear palette drag
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex = null) => {
    e.preventDefault();
    if (!draggedComponent) return;

    const newComponent = {
      id: Date.now().toString(),
      type: draggedComponent,
      props: getDefaultProps(draggedComponent)
    };

    const newLayout = [...formData.layout];
    if (targetIndex !== null) {
      newLayout.splice(targetIndex, 0, newComponent);
    } else {
      newLayout.push(newComponent);
    }

    setFormData({ ...formData, layout: newLayout });
    setDraggedComponent(null);
  };

  const handleComponentDrop = (e, targetIndex) => {
    e.preventDefault();
    
    // If dragging an existing component (reordering)
    if (draggedComponentId !== null) {
      const sourceIndex = formData.layout.findIndex(c => c.id === draggedComponentId);
      if (sourceIndex === -1 || sourceIndex === targetIndex) {
        setDraggedComponentId(null);
        return;
      }

      const newLayout = [...formData.layout];
      const [movedComponent] = newLayout.splice(sourceIndex, 1);
      
      // Adjust target index if source was before target
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      newLayout.splice(adjustedTargetIndex, 0, movedComponent);
      
      setFormData({ ...formData, layout: newLayout });
      setDraggedComponentId(null);
      return;
    }

    // If dragging a new component from palette
    if (!draggedComponent) return;

    const newComponent = {
      id: Date.now().toString(),
      type: draggedComponent,
      props: getDefaultProps(draggedComponent)
    };

    const newLayout = [...formData.layout];
    newLayout.splice(targetIndex, 0, newComponent);
    setFormData({ ...formData, layout: newLayout });
    setDraggedComponent(null);
  };

  const getDefaultProps = (type) => {
    const defaults = {
      [COMPONENT_TYPES.HEADER]: { 
        title: 'Event Title', 
        showFeatured: true,
        backgroundColor: '#667eea',
        useGradient: true,
        gradientStart: '#667eea',
        gradientEnd: '#764ba2',
        textColor: '#ffffff'
      },
      [COMPONENT_TYPES.IMAGE]: { src: '', alt: 'Event Image', height: '300px' },
      [COMPONENT_TYPES.TEXT]: { content: 'Text content here', heading: 'About This Event' },
      [COMPONENT_TYPES.BUTTON]: { 
        text: 'Open in Utilita App', 
        link: '', 
        style: 'gradient',
        backgroundColor: '#667eea',
        gradientStart: '#667eea',
        gradientEnd: '#764ba2',
        textColor: '#ffffff'
      },
      [COMPONENT_TYPES.INFO_SECTION]: { 
        showVenue: true, 
        showDate: true, 
        showPricing: true,
        venueIcon: '📍',
        dateIcon: '📅',
        pricingIcon: '💰',
        venueLabel: 'VENUE',
        dateLabel: 'DATE & TIME',
        pricingLabel: 'PRICING',
        labelColor: '#666666',
        valueColor: '#333333',
        backgroundColor: 'transparent',
        layoutDirection: 'vertical',
        gap: 20,
        iconSize: 24,
        labelFontSize: 12,
        valueFontSize: 16
      },
      [COMPONENT_TYPES.GALLERY]: { images: [], columns: 4, showTitle: true },
      [COMPONENT_TYPES.TICKET_TYPES]: { showTitle: true, maxDisplay: 3 },
      [COMPONENT_TYPES.DOWNLOAD_BUTTONS]: { showIOS: true, showAndroid: true },
      [COMPONENT_TYPES.CONTAINER]: { padding: '20px', bgColor: 'transparent' },
      [COMPONENT_TYPES.DIVIDER]: { thickness: '1px', color: '#e0e0e0', margin: '20px 0' },
      [COMPONENT_TYPES.SPACER]: { height: '30px' },
      [COMPONENT_TYPES.FOOTER]: { text: 'Powered by Utilita', showAffiliate: true },
      [COMPONENT_TYPES.CATEGORY_BADGE]: { showLabel: true, style: 'badge' },
      [COMPONENT_TYPES.EVENT_TYPE_BADGE]: { showIcon: true, style: 'badge' },
      [COMPONENT_TYPES.TAGS]: { showAsChips: true, maxDisplay: 10 },
      [COMPONENT_TYPES.VENDOR_INFO]: { 
        showName: true, 
        showDescription: true, 
        showContact: false,
        nameColor: '#333333',
        descriptionColor: '#666666',
        contactColor: '#666666',
        backgroundColor: '#f8f9fa'
      },
      [COMPONENT_TYPES.ANALYTICS_STATS]: { showViews: true, showShares: true, showFavorites: true },
      [COMPONENT_TYPES.SOCIAL_SHARING]: { showFacebook: true, showTwitter: true, showWhatsApp: true, showInstagram: true },
      [COMPONENT_TYPES.EVENT_STATUS]: { showBadge: true, style: 'badge' },
      [COMPONENT_TYPES.DATE_RANGE]: { showStartDate: true, showEndDate: true, format: 'full' },
      [COMPONENT_TYPES.VIRTUAL_LINK]: { showPlatform: true, showLink: true },
      [COMPONENT_TYPES.POLICIES]: { showTerms: true, showRefund: true, showCancellation: true },
      [COMPONENT_TYPES.VERIFICATION_BADGE]: { showBadge: true, style: 'badge' },
      [COMPONENT_TYPES.COUNTDOWN_TIMER]: { 
        showLabel: true, 
        label: '⏰ Event Starts In',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        textColor: '#ffffff',
        borderRadius: '20px',
        padding: '24px'
      }
    };
    return defaults[type] || {};
  };

  const handleDeleteComponent = (componentId) => {
    setFormData({
      ...formData,
      layout: formData.layout.filter(c => c.id !== componentId)
    });
    if (selectedComponent?.id === componentId) {
      setSelectedComponent(null);
    }
  };

  const handleDuplicateComponent = (componentId) => {
    const component = formData.layout.find(c => c.id === componentId);
    if (component) {
      const newComponent = {
        ...component,
        id: Date.now().toString()
      };
      const index = formData.layout.findIndex(c => c.id === componentId);
      const newLayout = [...formData.layout];
      newLayout.splice(index + 1, 0, newComponent);
      setFormData({ ...formData, layout: newLayout });
    }
  };

  const handleUpdateComponentProps = (componentId, props) => {
    const updatedLayout = formData.layout.map(c =>
      c.id === componentId ? { ...c, props: { ...c.props, ...props } } : c
    );
    
    setFormData({
      ...formData,
      layout: updatedLayout
    });
    
    // Update selectedComponent to reflect the changes
    if (selectedComponent && selectedComponent.id === componentId) {
      const updatedComponent = updatedLayout.find(c => c.id === componentId);
      if (updatedComponent) {
        setSelectedComponent(updatedComponent);
      }
    }
  };

  const handleMoveComponent = (componentId, direction) => {
    const index = formData.layout.findIndex(c => c.id === componentId);
    if (index === -1) return;

    const newLayout = [...formData.layout];
    if (direction === 'up' && index > 0) {
      [newLayout[index - 1], newLayout[index]] = [newLayout[index], newLayout[index - 1]];
    } else if (direction === 'down' && index < newLayout.length - 1) {
      [newLayout[index], newLayout[index + 1]] = [newLayout[index + 1], newLayout[index]];
    }
    setFormData({ ...formData, layout: newLayout });
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Event Design Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Create Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading && templates.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow key={template._id} hover>
                        <TableCell>
                          <Typography variant="body1" fontWeight={500}>
                            {template.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {template.description || 'No description'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={template.isActive ? 'Active' : 'Inactive'}
                            color={template.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {template.isDefault ? (
                            <StarIcon color="warning" />
                          ) : (
                            <StarBorderIcon color="disabled" />
                          )}
                        </TableCell>
                        <TableCell>
                          {template.createdAt
                            ? format(new Date(template.createdAt), 'MMM dd, yyyy')
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(template)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Page Builder Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            height: '95vh',
            maxHeight: '95vh',
            width: '95vw',
            maxWidth: '95vw'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedTemplate ? 'Edit Design Template' : 'Create Design Template'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(95vh - 100px)', overflow: 'hidden', display: 'flex' }}>
          {/* Left Sidebar - Component Palette */}
          <Box
            sx={{
              width: 280,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              overflowY: 'auto',
              p: 2
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Components
            </Typography>
            <List dense>
              {COMPONENT_PALETTE.map((item) => (
                <ListItem
                  key={item.type}
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <Card
                    sx={{
                      width: '100%',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item.type)}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ color: 'primary.main' }}>{item.icon}</Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {item.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        </Box>
                        <DragIcon sx={{ color: 'text.disabled' }} />
                      </Stack>
                    </CardContent>
                  </Card>
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Design Settings */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Design Settings
            </Typography>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">Colors</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Primary"
                    type="color"
                    value={formData.design.primaryColor}
                    onChange={(e) => setFormData({
                      ...formData,
                      design: { ...formData.design, primaryColor: e.target.value }
                    })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Secondary"
                    type="color"
                    value={formData.design.secondaryColor}
                    onChange={(e) => setFormData({
                      ...formData,
                      design: { ...formData.design, secondaryColor: e.target.value }
                    })}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">Typography</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Font Family"
                    value={formData.design.fontFamily}
                    onChange={(e) => setFormData({
                      ...formData,
                      design: { ...formData.design, fontFamily: e.target.value }
                    })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Heading Size"
                    value={formData.design.headingFontSize}
                    onChange={(e) => setFormData({
                      ...formData,
                      design: { ...formData.design, headingFontSize: e.target.value }
                    })}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Center - Canvas */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              bgcolor: 'grey.100',
              p: 3,
              display: 'flex',
              justifyContent: 'center'
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <Box sx={{ width: '100%', maxWidth: 600 }}>
              <EventPageCanvas
                layout={formData.layout}
                design={formData.design}
                onComponentClick={setSelectedComponent}
                selectedComponent={selectedComponent}
                onDeleteComponent={handleDeleteComponent}
                onDuplicateComponent={handleDuplicateComponent}
                onMoveComponent={handleMoveComponent}
                onDrop={handleComponentDrop}
                onDragOver={handleDragOver}
                onDragStart={handleComponentDragStart}
              />
            </Box>
          </Box>

          {/* Right Sidebar - Properties Panel */}
          {propertiesPanelOpen && (
            <Box
              sx={{
                width: 320,
                borderLeft: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                overflowY: 'auto',
                p: 2
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Template Settings
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Template Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isDefault}
                      onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    />
                  }
                  label="Set as Default"
                />
              </Stack>

              {selectedComponent && (() => {
                // Always get the latest component from layout to ensure we have the most up-to-date props
                const currentComponent = formData.layout.find(c => c.id === selectedComponent.id) || selectedComponent;
                return (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Component Properties
                    </Typography>
                    <ComponentPropertiesPanel
                      component={currentComponent}
                      design={formData.design}
                      onUpdate={(props) => handleUpdateComponentProps(currentComponent.id, props)}
                    />
                  </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name}
          >
            {selectedTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedTemplate?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Canvas Component
const EventPageCanvas = ({
  layout,
  design,
  onComponentClick,
  selectedComponent,
  onDeleteComponent,
  onDuplicateComponent,
  onMoveComponent,
  onDrop,
  onDragOver,
  onDragStart
}) => {
  const gradient = design?.useGradient !== false
    ? `linear-gradient(${design?.gradientDirection || '135deg'}, ${design?.gradientStart || design?.primaryColor || '#667eea'} 0%, ${design?.gradientEnd || design?.secondaryColor || '#764ba2'} 100%)`
    : design?.backgroundColor || '#ffffff';

  // Debug: Log layout
  console.log('Canvas layout:', layout);

  if (!layout || !Array.isArray(layout)) {
    return (
      <Box
        sx={{
          height: 200,
          border: '2px dashed',
          borderColor: 'grey.400',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          bgcolor: 'background.paper',
          borderRadius: 2
        }}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <Typography>No layout defined. Drag components here to build your page.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: design?.backgroundColor || '#ffffff',
        borderRadius: design?.borderRadius || '24px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        fontFamily: design?.fontFamily || 'system-ui, -apple-system, sans-serif',
        minHeight: 400
      }}
    >
      {layout.length > 0 ? layout.map((component, index) => {
        // Ensure component has required fields
        if (!component || !component.type) {
          console.warn('Invalid component at index', index, component);
          return null;
        }
        
        return (
          <React.Fragment key={component.id || `comp-${index}`}>
            <ComponentRenderer
              component={component}
              design={design}
              gradient={gradient}
              isSelected={selectedComponent?.id === component.id}
              onClick={() => onComponentClick(component)}
              onDelete={() => onDeleteComponent(component.id)}
              onDuplicate={() => onDuplicateComponent(component.id)}
              onMoveUp={() => onMoveComponent(component.id, 'up')}
              onMoveDown={() => onMoveComponent(component.id, 'down')}
              onDrop={(e) => onDrop(e, index)}
              onDragOver={onDragOver}
              onDragStart={onDragStart}
            />
          {index < layout.length - 1 && (
            <Box
              sx={{
                height: 40,
                border: '2px dashed',
                borderColor: 'primary.light',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.selected' }
              }}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, index + 1)}
            >
              <Typography variant="caption" color="primary">
                Drop component here
              </Typography>
            </Box>
          )}
          </React.Fragment>
        );
      }).filter(Boolean) : (
        <Box
          sx={{
            height: 200,
            border: '2px dashed',
            borderColor: 'grey.400',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary'
          }}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          <Typography>Drag components here to build your page</Typography>
        </Box>
      )}
    </Box>
  );
};

// Component Renderer
const ComponentRenderer = ({
  component,
  design,
  gradient,
  isSelected,
  onClick,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onDrop,
  onDragOver,
  onDragStart
}) => {
  const renderComponent = () => {
    switch (component.type) {
      case COMPONENT_TYPES.HEADER:
        // Get header styling from component props or fallback to design/default
        const headerUseGradient = component.props?.useGradient !== false
        const headerBgColor = component.props?.backgroundColor || design?.primaryColor || '#667eea'
        const headerGradStart = component.props?.gradientStart || design?.gradientStart || design?.primaryColor || '#667eea'
        const headerGradEnd = component.props?.gradientEnd || design?.gradientEnd || design?.secondaryColor || '#764ba2'
        const headerTextColor = component.props?.textColor || '#ffffff'
        
        // Build background style
        const headerBg = headerUseGradient
          ? `linear-gradient(135deg, ${headerGradStart} 0%, ${headerGradEnd} 100%)`
          : headerBgColor
        
        return (
          <Box
            sx={{
              background: headerBg,
              color: headerTextColor,
              p: 3,
              textAlign: 'center',
              position: 'relative'
            }}
          >
            <Typography
              sx={{
                fontSize: design.headingFontSize || '28px',
                fontWeight: 700,
                mb: 1,
                color: headerTextColor
              }}
            >
              {component.props.title || 'Event Title'}
            </Typography>
            {component.props.showFeatured && (
              <Chip label="🔥 Featured" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
            )}
          </Box>
        );
      case COMPONENT_TYPES.IMAGE:
        return (
          <Box
            sx={{
              width: '100%',
              height: component.props.height || '300px',
              bgcolor: 'grey.300',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'grey.600',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {component.props.src ? (
              <img
                src={component.props.src}
                alt={component.props.alt || 'Event Image'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <Typography variant="body2">Cover Image</Typography>
            )}
          </Box>
        );
      case COMPONENT_TYPES.INFO_SECTION:
        return (
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            {component.props.showVenue && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: 24, lineHeight: 1 }}>📍</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#666', 
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'block',
                      mb: 0.5
                    }}
                  >
                    Venue
                  </Typography>
                  <Typography sx={{ 
                    fontSize: design.bodyFontSize || '16px', 
                    color: design.textColor || '#333',
                    fontWeight: 500,
                    lineHeight: 1.4
                  }}>
                    Orchid and Lantana Hall, Eko Hotel and Suites, Victoria Island, Lagos
                  </Typography>
                </Box>
              </Box>
            )}
            {component.props.showDate && (
              <Box sx={{ display: 'flex', gap: 2, mb: component.props.showPricing ? 2.5 : 0, alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: 24, lineHeight: 1 }}>📅</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#666', 
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'block',
                      mb: 0.5
                    }}
                  >
                    Date & Time
                  </Typography>
                  <Typography sx={{ 
                    fontSize: design.bodyFontSize || '16px', 
                    color: design.textColor || '#333',
                    fontWeight: 500,
                    lineHeight: 1.4
                  }}>
                    Thursday, January 8, 2026 at 08:30 AM
                  </Typography>
                </Box>
              </Box>
            )}
            {component.props.showPricing && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: 24, lineHeight: 1 }}>💰</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#666', 
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'block',
                      mb: 0.5
                    }}
                  >
                    Pricing
                  </Typography>
                  <Typography sx={{ 
                    fontSize: '18px', 
                    color: '#10b981',
                    fontWeight: 700
                  }}>
                    FREE ENTRY
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        );
      case COMPONENT_TYPES.TEXT:
        return (
          <Box sx={{ p: 3 }}>
            {component.props.heading && (
              <Typography
                variant="h6"
                sx={{
                  fontSize: '20px',
                  color: design.textColor || '#333',
                  fontWeight: 700,
                  mb: 1.5
                }}
              >
                {component.props.heading}
              </Typography>
            )}
            <Typography 
              sx={{ 
                fontSize: design.bodyFontSize || '16px', 
                color: '#666',
                lineHeight: 1.6
              }}
            >
              {component.props.content || 'The AFRIMA business summit is the African Music Business Summit (AMBS), an event held as part of the larger All Africa Music Awards (AFRIMA) festivities. The next AMBS aims to gather music industry stakeholders to discuss innovation and sustainability within the African music ecosystem.'}
            </Typography>
          </Box>
        );
      case COMPONENT_TYPES.BUTTON:
        const buttonStyleType = component.props.style || design.buttonStyle || 'gradient';
        const buttonBgColor = component.props?.backgroundColor || design?.primaryColor || '#667eea';
        const buttonGradStart = component.props?.gradientStart || design?.gradientStart || design?.primaryColor || '#667eea';
        const buttonGradEnd = component.props?.gradientEnd || design?.gradientEnd || design?.secondaryColor || '#764ba2';
        const buttonTextColor = component.props?.textColor || '#ffffff';
        
        // Build button style based on style type
        let buttonStyleObj = {};
        if (buttonStyleType === 'gradient') {
          buttonStyleObj.background = `linear-gradient(135deg, ${buttonGradStart} 0%, ${buttonGradEnd} 100%)`;
          buttonStyleObj.color = buttonTextColor;
          buttonStyleObj.border = 'none';
          buttonStyleObj.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
        } else if (buttonStyleType === 'solid') {
          buttonStyleObj.background = buttonBgColor;
          buttonStyleObj.color = buttonTextColor;
          buttonStyleObj.border = 'none';
          buttonStyleObj.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
        } else if (buttonStyleType === 'outline') {
          buttonStyleObj.background = 'transparent';
          buttonStyleObj.color = buttonBgColor;
          buttonStyleObj.border = `2px solid ${buttonBgColor}`;
        }
        
        return (
          <Box sx={{ p: 3 }}>
            <Button
              fullWidth
              sx={{
                ...buttonStyleObj,
                py: 2.25,
                borderRadius: design.buttonBorderRadius || '12px',
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': {
                  ...buttonStyleObj,
                  opacity: 0.9,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
                }
              }}
            >
              {component.props.text || 'Open in Utilita App'}
            </Button>
          </Box>
        );
      case COMPONENT_TYPES.GALLERY:
        const galleryColumns = component.props.columns || 4;
        return (
          <Box sx={{ p: 3 }}>
            {component.props.showTitle !== false && (
              <Typography variant="h6" sx={{ mb: 2, fontSize: '20px', color: design.textColor || '#333', fontWeight: 700 }}>
                📸 Event Gallery
              </Typography>
            )}
            <Grid container spacing={2}>
              {[1, 2, 3, 4].slice(0, galleryColumns).map((i) => (
                <Grid item xs={12 / galleryColumns} key={i}>
                  <Box
                    sx={{
                      aspectRatio: '1',
                      bgcolor: 'grey.300',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.8 }
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'grey.600' }}>Gallery Image {i}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      case COMPONENT_TYPES.TICKET_TYPES:
        return (
          <Box sx={{ p: 3 }}>
            {component.props.showTitle !== false && (
              <Typography variant="h6" sx={{ mb: 2, fontSize: '20px', color: design.textColor || '#333', fontWeight: 700 }}>
                Ticket Types
              </Typography>
            )}
            <Grid container spacing={2}>
              {[1, 2, 3].slice(0, component.props.maxDisplay || 3).map((i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Box
                    sx={{
                      bgcolor: 'grey.50',
                      p: 2,
                      borderRadius: 2,
                      textAlign: 'center',
                      border: '2px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                      Standard Ticket
                    </Typography>
                    <Typography variant="h6" sx={{ color: design.primaryColor || '#667eea', fontWeight: 700 }}>
                      FREE
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      case COMPONENT_TYPES.DOWNLOAD_BUTTONS:
        return (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {component.props.showIOS !== false && (
                <Grid item xs={component.props.showAndroid !== false ? 6 : 12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'grey.200',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.100' }
                    }}
                  >
                    <Typography sx={{ fontSize: 20 }}>🍎</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Download for iOS
                    </Typography>
                  </Box>
                </Grid>
              )}
              {component.props.showAndroid !== false && (
                <Grid item xs={component.props.showIOS !== false ? 6 : 12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'grey.200',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.100' }
                    }}
                  >
                    <Typography sx={{ fontSize: 20 }}>🤖</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Download for Android
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      case COMPONENT_TYPES.DIVIDER:
        return (
          <Box
            sx={{
              height: component.props.thickness || '1px',
              bgcolor: component.props.color || '#e0e0e0',
              my: component.props.margin ? undefined : 2,
              mx: 0,
              ...(component.props.margin && {
                margin: component.props.margin
              })
            }}
          />
        );
      case COMPONENT_TYPES.SPACER:
        return (
          <Box sx={{ height: component.props.height || '30px' }} />
        );
      case COMPONENT_TYPES.FOOTER:
        return (
          <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontSize: '14px', color: '#999' }}>
              {component.props.text || 'Powered by Utilita'}
            </Typography>
          </Box>
        );
      case COMPONENT_TYPES.CATEGORY_BADGE:
        return (
          <Box sx={{ p: 3 }}>
            <Chip
              label={component.props.showLabel !== false ? 'Category: Business' : 'Business'}
              sx={{
                bgcolor: design.primaryColor || '#667eea',
                color: 'white',
                fontWeight: 600,
                fontSize: '12px'
              }}
            />
          </Box>
        );
      case COMPONENT_TYPES.EVENT_TYPE_BADGE:
        return (
          <Box sx={{ p: 3 }}>
            <Chip
              label="🌐 Hybrid"
              sx={{
                bgcolor: design.secondaryColor || '#764ba2',
                color: 'white',
                fontWeight: 600,
                fontSize: '12px'
              }}
            />
          </Box>
        );
      case COMPONENT_TYPES.TAGS:
        return (
          <Box sx={{ p: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['#AFRIMA', '#Music', '#Business'].slice(0, component.props.maxDisplay || 10).map((tag, i) => (
              <Chip
                key={i}
                label={tag}
                size="small"
                sx={{
                  bgcolor: 'grey.100',
                  color: '#333',
                  fontWeight: 500
                }}
              />
            ))}
          </Box>
        );
      case COMPONENT_TYPES.VENDOR_INFO:
        const vendorNameColor = component.props?.nameColor || design?.textColor || '#333333';
        const vendorDescColor = component.props?.descriptionColor || '#666666';
        const vendorContactColor = component.props?.contactColor || '#666666';
        const vendorBgColor = component.props?.backgroundColor || '#f8f9fa';
        
        return (
          <Box sx={{ p: 3, bgcolor: vendorBgColor, borderRadius: 2 }}>
            {component.props.showName !== false && (
              <Typography variant="h6" sx={{ mb: 1, fontSize: '18px', fontWeight: 700, color: vendorNameColor }}>
                All Africa Music Awards
              </Typography>
            )}
            {component.props.showDescription !== false && (
              <Typography variant="body2" sx={{ mb: 1, color: vendorDescColor }}>
                The All Africa Music Award is an annual award that celebrates and reward African music talent and creativity.
              </Typography>
            )}
            {component.props.showContact && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: vendorContactColor, display: 'block' }}>
                  📧 info@afrima.org
                </Typography>
                <Typography variant="caption" sx={{ color: vendorContactColor, display: 'block' }}>
                  📞 07053931361
                </Typography>
              </Box>
            )}
          </Box>
        );
      case COMPONENT_TYPES.ANALYTICS_STATS:
        return (
          <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, display: 'flex', gap: 3 }}>
            {component.props.showViews !== false && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: design.primaryColor || '#667eea', fontWeight: 700 }}>
                  180
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>Views</Typography>
              </Box>
            )}
            {component.props.showShares !== false && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: design.primaryColor || '#667eea', fontWeight: 700 }}>
                  0
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>Shares</Typography>
              </Box>
            )}
            {component.props.showFavorites !== false && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: design.primaryColor || '#667eea', fontWeight: 700 }}>
                  0
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>Favorites</Typography>
              </Box>
            )}
          </Box>
        );
      case COMPONENT_TYPES.SOCIAL_SHARING:
        return (
          <Box sx={{ p: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {component.props.showFacebook !== false && (
              <Button
                size="small"
                variant="contained"
                sx={{ bgcolor: '#1877f2', '&:hover': { bgcolor: '#1565c0' } }}
              >
                Facebook
              </Button>
            )}
            {component.props.showTwitter !== false && (
              <Button
                size="small"
                variant="contained"
                sx={{ bgcolor: '#1da1f2', '&:hover': { bgcolor: '#0d8bd9' } }}
              >
                Twitter
              </Button>
            )}
            {component.props.showWhatsApp !== false && (
              <Button
                size="small"
                variant="contained"
                sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#20ba5a' } }}
              >
                WhatsApp
              </Button>
            )}
            {component.props.showInstagram !== false && (
              <Button
                size="small"
                variant="contained"
                sx={{ bgcolor: '#e4405f', '&:hover': { bgcolor: '#c13584' } }}
              >
                Instagram
              </Button>
            )}
          </Box>
        );
      case COMPONENT_TYPES.EVENT_STATUS:
        return (
          <Box sx={{ p: 3 }}>
            <Chip
              label="Published"
              sx={{
                bgcolor: '#2196f3',
                color: 'white',
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'capitalize'
              }}
            />
          </Box>
        );
      case COMPONENT_TYPES.DATE_RANGE:
        return (
          <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            {component.props.showStartDate !== false && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>
                  Start:
                </Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, mt: 0.5 }}>
                  Thursday, January 8, 2026 at 08:30 AM
                </Typography>
              </Box>
            )}
            {component.props.showEndDate !== false && (
              <Box>
                <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>
                  End:
                </Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, mt: 0.5 }}>
                  Thursday, January 8, 2026 at 03:30 PM
                </Typography>
              </Box>
            )}
          </Box>
        );
      case COMPONENT_TYPES.VIRTUAL_LINK:
        return (
          <Box sx={{ p: 3, bgcolor: 'primary.light', borderRadius: 2 }}>
            {component.props.showPlatform !== false && (
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Platform: Zoom
              </Typography>
            )}
            {component.props.showLink !== false && (
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 1 }}
              >
                Join Virtual Event
              </Button>
            )}
          </Box>
        );
      case COMPONENT_TYPES.POLICIES:
        return (
          <Box sx={{ p: 3 }}>
            {component.props.showTerms && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 700, mb: 1 }}>
                  Terms & Conditions
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                  Sample terms and conditions text...
                </Typography>
              </Box>
            )}
            {component.props.showRefund && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 700, mb: 1 }}>
                  Refund Policy
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                  Sample refund policy text...
                </Typography>
              </Box>
            )}
            {component.props.showCancellation && (
              <Box>
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 700, mb: 1 }}>
                  Cancellation Policy
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                  Sample cancellation policy text...
                </Typography>
              </Box>
            )}
          </Box>
        );
      case COMPONENT_TYPES.VERIFICATION_BADGE:
        return (
          <Box sx={{ p: 3 }}>
            <Chip
              icon={<CheckCircleIcon />}
              label="✓ Verified Event"
              sx={{
                bgcolor: '#4caf50',
                color: 'white',
                fontWeight: 600,
                fontSize: '12px'
              }}
            />
          </Box>
        );
      case COMPONENT_TYPES.COUNTDOWN_TIMER:
        return (
          <Box sx={{ 
            p: 3,
            background: component.props?.backgroundColor || 'rgba(233, 69, 96, 0.1)',
            borderRadius: component.props?.borderRadius || '20px',
            textAlign: 'center'
          }}>
            {component.props?.showLabel !== false && (
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {component.props?.label || '⏰ Event Starts In'}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center', minWidth: '60px' }}>
                <Box sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  borderRadius: '12px',
                  p: 1.5,
                  mb: 1,
                  fontFamily: 'monospace',
                  fontSize: '24px',
                  fontWeight: 700
                }}>
                  00
                </Box>
                <Typography variant="caption" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                  Days
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, alignSelf: 'flex-start', pt: 1.5 }}>
                :
              </Typography>
              <Box sx={{ textAlign: 'center', minWidth: '60px' }}>
                <Box sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  borderRadius: '12px',
                  p: 1.5,
                  mb: 1,
                  fontFamily: 'monospace',
                  fontSize: '24px',
                  fontWeight: 700
                }}>
                  00
                </Box>
                <Typography variant="caption" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                  Hours
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, alignSelf: 'flex-start', pt: 1.5 }}>
                :
              </Typography>
              <Box sx={{ textAlign: 'center', minWidth: '60px' }}>
                <Box sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  borderRadius: '12px',
                  p: 1.5,
                  mb: 1,
                  fontFamily: 'monospace',
                  fontSize: '24px',
                  fontWeight: 700
                }}>
                  00
                </Box>
                <Typography variant="caption" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                  Minutes
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, alignSelf: 'flex-start', pt: 1.5 }}>
                :
              </Typography>
              <Box sx={{ textAlign: 'center', minWidth: '60px' }}>
                <Box sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  borderRadius: '12px',
                  p: 1.5,
                  mb: 1,
                  fontFamily: 'monospace',
                  fontSize: '24px',
                  fontWeight: 700
                }}>
                  00
                </Box>
                <Typography variant="caption" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                  Seconds
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      default:
        return <Box sx={{ p: 2 }}>Unknown component</Box>;
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        border: isSelected ? '2px solid' : '2px solid transparent',
        borderColor: isSelected ? 'primary.main' : 'transparent',
        '&:hover': {
          borderColor: isSelected ? 'primary.main' : 'grey.400'
        },
        cursor: 'move'
      }}
      onClick={onClick}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        if (onDragStart) {
          onDragStart(e, component.id);
        }
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: 0,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 1,
            display: 'flex',
            gap: 0.5,
            zIndex: 10,
            p: 0.5
          }}
        >
          <Tooltip title="Move Up">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} sx={{ color: 'white' }}>
              <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Move Down">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} sx={{ color: 'white' }}>
              <ExpandMoreIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDuplicate(); }} sx={{ color: 'white' }}>
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ color: 'white' }}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {renderComponent()}
    </Box>
  );
};

// Component Properties Panel
const ComponentPropertiesPanel = ({ component, design, onUpdate }) => {
  const handleChange = (key, value) => {
    onUpdate({ [key]: value });
  };

  return (
    <Stack spacing={2}>
      {component.type === COMPONENT_TYPES.HEADER && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Title"
            value={component.props.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showFeatured !== false}
                onChange={(e) => handleChange('showFeatured', e.target.checked)}
              />
            }
            label="Show Featured Badge"
          />
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Background Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Background Color"
              value={component.props.backgroundColor || '#667eea'}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Color Code"
              value={component.props.backgroundColor || '#667eea'}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              placeholder="#667eea"
            />
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={component.props.useGradient !== false}
                onChange={(e) => handleChange('useGradient', e.target.checked)}
              />
            }
            label="Use Gradient"
          />
          {component.props.useGradient !== false && (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="color"
                  label="Gradient Start"
                  value={component.props.gradientStart || '#667eea'}
                  onChange={(e) => handleChange('gradientStart', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { height: '40px' } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Start Color"
                  value={component.props.gradientStart || '#667eea'}
                  onChange={(e) => handleChange('gradientStart', e.target.value)}
                  placeholder="#667eea"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="color"
                  label="Gradient End"
                  value={component.props.gradientEnd || '#764ba2'}
                  onChange={(e) => handleChange('gradientEnd', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { height: '40px' } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="End Color"
                  value={component.props.gradientEnd || '#764ba2'}
                  onChange={(e) => handleChange('gradientEnd', e.target.value)}
                  placeholder="#764ba2"
                />
              </Box>
            </>
          )}
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Text Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Text Color"
              value={component.props.textColor || '#ffffff'}
              onChange={(e) => handleChange('textColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Color Code"
              value={component.props.textColor || '#ffffff'}
              onChange={(e) => handleChange('textColor', e.target.value)}
              placeholder="#ffffff"
            />
          </Box>
        </>
      )}
      {component.type === COMPONENT_TYPES.IMAGE && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Image URL"
            value={component.props.src || ''}
            onChange={(e) => handleChange('src', e.target.value)}
            placeholder="https://example.com/image.jpg"
          />
          <TextField
            fullWidth
            size="small"
            label="Alt Text"
            value={component.props.alt || ''}
            onChange={(e) => handleChange('alt', e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Height"
            value={component.props.height || '300px'}
            onChange={(e) => handleChange('height', e.target.value)}
            placeholder="300px"
          />
        </>
      )}
      {component.type === COMPONENT_TYPES.TEXT && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Heading"
            value={component.props.heading || ''}
            onChange={(e) => handleChange('heading', e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Content"
            multiline
            rows={4}
            value={component.props.content || ''}
            onChange={(e) => handleChange('content', e.target.value)}
          />
        </>
      )}
      {component.type === COMPONENT_TYPES.BUTTON && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Button Text"
            value={component.props.text || ''}
            onChange={(e) => handleChange('text', e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Link (optional)"
            value={component.props.link || ''}
            onChange={(e) => handleChange('link', e.target.value)}
            placeholder="Leave empty for deep link"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Button Style</InputLabel>
            <Select
              value={component.props.style || 'gradient'}
              onChange={(e) => handleChange('style', e.target.value)}
            >
              <MenuItem value="gradient">Gradient</MenuItem>
              <MenuItem value="solid">Solid</MenuItem>
              <MenuItem value="outline">Outline</MenuItem>
            </Select>
          </FormControl>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Button Colors
          </Typography>
          {component.props.style === 'gradient' ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="color"
                  label="Gradient Start"
                  value={component.props.gradientStart || design?.gradientStart || design?.primaryColor || '#667eea'}
                  onChange={(e) => handleChange('gradientStart', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { height: '40px' } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Start Color"
                  value={component.props.gradientStart || design?.gradientStart || design?.primaryColor || '#667eea'}
                  onChange={(e) => handleChange('gradientStart', e.target.value)}
                  placeholder="#667eea"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="color"
                  label="Gradient End"
                  value={component.props.gradientEnd || design?.gradientEnd || design?.secondaryColor || '#764ba2'}
                  onChange={(e) => handleChange('gradientEnd', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { height: '40px' } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="End Color"
                  value={component.props.gradientEnd || design?.gradientEnd || design?.secondaryColor || '#764ba2'}
                  onChange={(e) => handleChange('gradientEnd', e.target.value)}
                  placeholder="#764ba2"
                />
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                type="color"
                label="Background Color"
                value={component.props.backgroundColor || design?.primaryColor || '#667eea'}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { height: '40px' } }}
              />
              <TextField
                fullWidth
                size="small"
                label="Color Code"
                value={component.props.backgroundColor || design?.primaryColor || '#667eea'}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                placeholder="#667eea"
              />
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Text Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Text Color"
              value={component.props.textColor || '#ffffff'}
              onChange={(e) => handleChange('textColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Color Code"
              value={component.props.textColor || '#ffffff'}
              onChange={(e) => handleChange('textColor', e.target.value)}
              placeholder="#ffffff"
            />
          </Box>
        </>
      )}
      {component.type === COMPONENT_TYPES.INFO_SECTION && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Display Options
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showVenue !== false}
                onChange={(e) => handleChange('showVenue', e.target.checked)}
              />
            }
            label="Show Venue"
          />
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showDate !== false}
                onChange={(e) => handleChange('showDate', e.target.checked)}
              />
            }
            label="Show Date"
          />
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showPricing !== false}
                onChange={(e) => handleChange('showPricing', e.target.checked)}
              />
            }
            label="Show Pricing"
          />
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Custom Labels
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Venue Label"
            value={component.props.venueLabel || 'VENUE'}
            onChange={(e) => handleChange('venueLabel', e.target.value)}
            placeholder="VENUE"
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Date Label"
            value={component.props.dateLabel || 'DATE & TIME'}
            onChange={(e) => handleChange('dateLabel', e.target.value)}
            placeholder="DATE & TIME"
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Pricing Label"
            value={component.props.pricingLabel || 'PRICING'}
            onChange={(e) => handleChange('pricingLabel', e.target.value)}
            placeholder="PRICING"
          />
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Custom Icons
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Venue Icon"
            value={component.props.venueIcon || '📍'}
            onChange={(e) => handleChange('venueIcon', e.target.value)}
            placeholder="📍"
            helperText="Emoji or icon text"
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Date Icon"
            value={component.props.dateIcon || '📅'}
            onChange={(e) => handleChange('dateIcon', e.target.value)}
            placeholder="📅"
            helperText="Emoji or icon text"
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Pricing Icon"
            value={component.props.pricingIcon || '💰'}
            onChange={(e) => handleChange('pricingIcon', e.target.value)}
            placeholder="💰"
            helperText="Emoji or icon text"
          />
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Colors
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Label Color"
              value={component.props.labelColor || design?.textColor || '#666666'}
              onChange={(e) => handleChange('labelColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Label Color Code"
              value={component.props.labelColor || design?.textColor || '#666666'}
              onChange={(e) => handleChange('labelColor', e.target.value)}
              placeholder="#666666"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Value Color"
              value={component.props.valueColor || design?.textColor || '#333333'}
              onChange={(e) => handleChange('valueColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Value Color Code"
              value={component.props.valueColor || design?.textColor || '#333333'}
              onChange={(e) => handleChange('valueColor', e.target.value)}
              placeholder="#333333"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Background Color"
              value={component.props.backgroundColor || 'transparent'}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Background Color Code"
              value={component.props.backgroundColor || 'transparent'}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              placeholder="transparent"
            />
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Layout & Spacing
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Layout Direction</InputLabel>
            <Select
              value={component.props.layoutDirection || 'vertical'}
              onChange={(e) => handleChange('layoutDirection', e.target.value)}
              label="Layout Direction"
            >
              <MenuItem value="vertical">Vertical</MenuItem>
              <MenuItem value="horizontal">Horizontal</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Gap Between Items (px)"
            value={component.props.gap || 20}
            onChange={(e) => handleChange('gap', parseInt(e.target.value) || 20)}
            inputProps={{ min: 0, max: 100 }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Icon Size (px)"
            value={component.props.iconSize || 24}
            onChange={(e) => handleChange('iconSize', parseInt(e.target.value) || 24)}
            inputProps={{ min: 12, max: 48 }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Label Font Size (px)"
            value={component.props.labelFontSize || 12}
            onChange={(e) => handleChange('labelFontSize', parseInt(e.target.value) || 12)}
            inputProps={{ min: 8, max: 24 }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Value Font Size (px)"
            value={component.props.valueFontSize || 16}
            onChange={(e) => handleChange('valueFontSize', parseInt(e.target.value) || 16)}
            inputProps={{ min: 10, max: 32 }}
          />
        </>
      )}
      {component.type === COMPONENT_TYPES.GALLERY && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showTitle !== false}
                onChange={(e) => handleChange('showTitle', e.target.checked)}
              />
            }
            label="Show Title"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Columns</InputLabel>
            <Select
              value={component.props.columns || 4}
              onChange={(e) => handleChange('columns', e.target.value)}
            >
              <MenuItem value={2}>2 Columns</MenuItem>
              <MenuItem value={3}>3 Columns</MenuItem>
              <MenuItem value={4}>4 Columns</MenuItem>
            </Select>
          </FormControl>
        </>
      )}
      {component.type === COMPONENT_TYPES.TICKET_TYPES && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showTitle !== false}
                onChange={(e) => handleChange('showTitle', e.target.checked)}
              />
            }
            label="Show Title"
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Max Display"
            value={component.props.maxDisplay || 3}
            onChange={(e) => handleChange('maxDisplay', parseInt(e.target.value))}
            inputProps={{ min: 1, max: 6 }}
          />
        </>
      )}
      {component.type === COMPONENT_TYPES.DOWNLOAD_BUTTONS && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showIOS !== false}
                onChange={(e) => handleChange('showIOS', e.target.checked)}
              />
            }
            label="Show iOS Button"
          />
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showAndroid !== false}
                onChange={(e) => handleChange('showAndroid', e.target.checked)}
              />
            }
            label="Show Android Button"
          />
        </>
      )}
      {component.type === COMPONENT_TYPES.DIVIDER && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Thickness"
            value={component.props.thickness || '1px'}
            onChange={(e) => handleChange('thickness', e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            type="color"
            label="Color"
            value={component.props.color || '#e0e0e0'}
            onChange={(e) => handleChange('color', e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Margin"
            value={component.props.margin || '20px 0'}
            onChange={(e) => handleChange('margin', e.target.value)}
            placeholder="20px 0"
          />
        </>
      )}
      {component.type === COMPONENT_TYPES.SPACER && (
        <TextField
          fullWidth
          size="small"
          label="Height"
          value={component.props.height || '30px'}
          onChange={(e) => handleChange('height', e.target.value)}
          placeholder="30px"
        />
      )}
      {component.type === COMPONENT_TYPES.FOOTER && (
        <>
          <TextField
            fullWidth
            size="small"
            label="Footer Text"
            value={component.props.text || ''}
            onChange={(e) => handleChange('text', e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showAffiliate !== false}
                onChange={(e) => handleChange('showAffiliate', e.target.checked)}
              />
            }
            label="Show Affiliate Note"
          />
        </>
      )}
      {component.type === COMPONENT_TYPES.VENDOR_INFO && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showName !== false}
                onChange={(e) => handleChange('showName', e.target.checked)}
              />
            }
            label="Show Name"
          />
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showDescription !== false}
                onChange={(e) => handleChange('showDescription', e.target.checked)}
              />
            }
            label="Show Description"
          />
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showContact || false}
                onChange={(e) => handleChange('showContact', e.target.checked)}
              />
            }
            label="Show Contact Info"
          />
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Text Colors
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Name Color"
              value={component.props.nameColor || design?.textColor || '#333333'}
              onChange={(e) => handleChange('nameColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Name Color Code"
              value={component.props.nameColor || design?.textColor || '#333333'}
              onChange={(e) => handleChange('nameColor', e.target.value)}
              placeholder="#333333"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Description Color"
              value={component.props.descriptionColor || '#666666'}
              onChange={(e) => handleChange('descriptionColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Description Color Code"
              value={component.props.descriptionColor || '#666666'}
              onChange={(e) => handleChange('descriptionColor', e.target.value)}
              placeholder="#666666"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Contact Color"
              value={component.props.contactColor || '#666666'}
              onChange={(e) => handleChange('contactColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Contact Color Code"
              value={component.props.contactColor || '#666666'}
              onChange={(e) => handleChange('contactColor', e.target.value)}
              placeholder="#666666"
            />
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Background Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Background Color"
              value={component.props.backgroundColor || '#f8f9fa'}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Background Color Code"
              value={component.props.backgroundColor || '#f8f9fa'}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              placeholder="#f8f9fa"
            />
          </Box>
        </>
      )}
      {component.type === COMPONENT_TYPES.COUNTDOWN_TIMER && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Countdown Timer Properties
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={component.props.showLabel !== false}
                onChange={(e) => handleChange('showLabel', e.target.checked)}
              />
            }
            label="Show Label"
          />
          <TextField
            fullWidth
            size="small"
            label="Label Text"
            value={component.props.label || '⏰ Event Starts In'}
            onChange={(e) => handleChange('label', e.target.value)}
            sx={{ mt: 2 }}
            placeholder="⏰ Event Starts In"
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Styling
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Background Color"
              value={component.props.backgroundColor || 'rgba(255, 255, 255, 0.15)'}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Background Color Code"
              value={component.props.backgroundColor || 'rgba(255, 255, 255, 0.15)'}
              onChange={(e) => handleChange('backgroundColor', e.target.value)}
              placeholder="rgba(255, 255, 255, 0.15)"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="color"
              label="Text Color"
              value={component.props.textColor || '#ffffff'}
              onChange={(e) => handleChange('textColor', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { height: '40px' } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Text Color Code"
              value={component.props.textColor || '#ffffff'}
              onChange={(e) => handleChange('textColor', e.target.value)}
              placeholder="#ffffff"
            />
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Border Radius"
            value={component.props.borderRadius || '20px'}
            onChange={(e) => handleChange('borderRadius', e.target.value)}
            sx={{ mb: 2 }}
            placeholder="20px"
          />
          <TextField
            fullWidth
            size="small"
            label="Padding"
            value={component.props.padding || '24px'}
            onChange={(e) => handleChange('padding', e.target.value)}
            placeholder="24px"
          />
        </>
      )}
    </Stack>
  );
};

export default EventDesignTemplates;
